import { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { Box, Card, Typography } from "@mui/joy";
import { useProject } from "../../../../hooks/query";
import { useColorScheme } from "@mui/joy/styles";

export default function LeadSourceChart() {
    const chartRef = useRef<HTMLDivElement | null>(null);
    const { useList } = useProject();
    const listProjectSvc = useList();
    const { mode } = useColorScheme();

    useEffect(() => {
        listProjectSvc.mutateAsync().catch(console.error);
    }, []);

    useEffect(() => {
        if (!chartRef.current || !listProjectSvc.data) return;

        const projects = listProjectSvc.data;

        // Count by Lead Source
        const leadCounts: Record<string, number> = {};
        projects.forEach((p: any) => {
            const source = p.Lead?.LeadSource?.Name || "Unknown";
            leadCounts[source] = (leadCounts[source] || 0) + 1;
        });

        const chartData = Object.entries(leadCounts).map(([name, value]) => ({ name, value }));

        const chart = echarts.init(chartRef.current, mode === "dark" ? "dark" : "light");

        chart.setOption({
            backgroundColor: "transparent",
            tooltip: {
                trigger: "item",
                formatter: "{b}: {c} ({d}%)",
            },
            labelLine: {
                show: false, // Hides label connector lines
            },
            legend: {
                orient: "vertical",
                left: 0,
                top: 20,
                itemGap: 12,
                itemWidth: 18,
                textStyle: {
                    fontFamily: "Albert Sans",
                    fontSize: 14,
                    color: mode === "dark" ? "#EAEAEA" : "#333333",
                },
            },
            grid: {
                top: 1000,
            },

            series: [
                {
                    emphasis: {
                        label: {
                            show: true,
                        },
                    },
                    name: "Lead Source",
                    type: "pie",
                    radius: ["20%", "65%"],
                    center: ["60%", "55%"],
                    roseType: "area",
                    label: {
                        fontFamily: "Albert Sans",
                        fontSize: 0,
                        formatter: "",
                        fontWeight: 500,
                        color: mode === "dark" ? "#EAEAEA" : "#333333",
                    },
                    itemStyle: {
                        borderRadius: 8,
                        borderColor: mode === "dark" ? "#1E1E1E" : "#fff",
                        borderWidth: 2,
                    },
                    color: [
                        "#2f6e3b",
                        "#3e8e41",
                        "#5fa95d",
                        "#87c97b",
                        "#afd6a4",
                        "#d2e7ca",
                        "#fac858",
                        "#ee6666",
                        "#73c0de",
                        "#91cc75",
                        "#5470c6",
                    ],
                    data: chartData,
                },
            ],
        });

        return () => chart.dispose();
    }, [listProjectSvc.data, mode]);

    return (
        <Card sx={{ mt: 3, p: 2, flex: 1, pb: 0, pr: 6 }}>
            <Typography level="title-lg" mb={1}>
                Lead Source Distribution
            </Typography>
            <Box
                ref={chartRef}
                sx={{
                    flex: 1, // Takes up remaining space
                    mt: "auto", // Ensures it sticks to the bottom if space allows
                }}
            />
        </Card>
    );
}
